/**
 * The always-visible transport bar.
 *
 * The library is long and the decks live above it, so by the time you have
 * found a track you want, the Load buttons are several screens away. This bar
 * sticks to the top and carries the few controls you need while browsing:
 * load, play/pause, what is on each deck, how long it has left, and the
 * crossfader.
 *
 * It owns no state. Loading goes through the existing `DeckPanel.load`, so the
 * whole-track semantics and the long-track memory warning stay in one place;
 * the crossfader reads and writes `Mixer`, exactly as the mixer panel does.
 * Both are views onto the same objects, which is why they cannot disagree —
 * there is nothing here to fall out of sync *with*.
 */

import type { Mixer } from "../audio/mixer.js";
import type { Track } from "../api.js";
import type { DeckPanel } from "./deck-panel.js";
import { duration as formatDuration, escapeHtml } from "./format.js";

/** Under this many seconds left, the countdown starts shouting. */
const RUNNING_OUT = 45;

export class TransportBar {
  private readonly panels: Record<string, DeckPanel> = {};

  constructor(
    private readonly root: HTMLElement,
    panels: DeckPanel[],
    private readonly mixer: Mixer,
    private readonly selected: () => Track | null,
  ) {
    for (const panel of panels) this.panels[panel.deck.id] = panel;
    const ids = panels.map((p) => p.deck.id);
    const [a, b] = ids;
    if (a === undefined || b === undefined) throw new Error("two decks are required");

    this.root.innerHTML = `
      ${this.deckCluster(a, "left")}
      <div class="tb-centre">
        <div class="tb-xf">
          <span class="muted">${escapeHtml(a)}</span>
          <input id="tb-xfader" type="range" min="-1" max="1" step="0.005" value="0"
                 aria-label="Crossfader" />
          <span class="muted">${escapeHtml(b)}</span>
        </div>
        <div class="tb-selected muted" id="tb-selected"></div>
      </div>
      ${this.deckCluster(b, "right")}
    `;
    this.bind();
  }

  /**
   * Load sits on the outside of each cluster, nearest the screen edge.
   *
   * It is the reason this bar exists, so it gets the corner rather than being
   * buried between the transport controls.
   */
  private deckCluster(id: string, side: "left" | "right"): string {
    const load = `<button type="button" class="tb-load" data-load="${id}" disabled
      title="Load the selected library track onto deck ${escapeHtml(id)}">Load ${escapeHtml(id)}</button>`;
    const play = `<button type="button" class="tb-play" data-play="${id}" disabled
      title="Play or pause deck ${escapeHtml(id)}">▶</button>`;
    const info = `<div class="tb-info">
        <div class="tb-title muted" data-title="${id}">empty</div>
        <div class="tb-time" data-time="${id}"></div>
      </div>`;

    const inner = side === "left" ? `${load}${play}${info}` : `${info}${play}${load}`;
    return `<div class="tb-deck tb-${side}" data-deck="${id}">${inner}</div>`;
  }

  private bind(): void {
    // Anything touched here is a hand on the controls, so auto-mix should step
    // back exactly as it does for the full panels (X2).
    this.root.addEventListener("pointerdown", () => this.onManualControl?.(), true);

    for (const button of this.root.querySelectorAll<HTMLElement>("button[data-load]")) {
      button.addEventListener("click", () => {
        const track = this.selected();
        const panel = this.panels[button.dataset["load"] as string];
        if (track && panel) void panel.load(track);
      });
    }

    for (const button of this.root.querySelectorAll<HTMLElement>("button[data-play]")) {
      button.addEventListener("click", () => {
        void this.panels[button.dataset["play"] as string]?.deck.togglePlay();
      });
    }

    const xf = this.xfader();
    xf.addEventListener("input", () => this.mixer.setCrossfader(Number(xf.value)));
    xf.addEventListener("dblclick", () => {
      this.mixer.setCrossfader(0);
      xf.value = "0";
    });
  }

  onManualControl: (() => void) | null = null;

  private xfader(): HTMLInputElement {
    return this.root.querySelector("#tb-xfader") as HTMLInputElement;
  }

  private q(selector: string): HTMLElement {
    return this.root.querySelector(selector) as HTMLElement;
  }

  /** Called once per animation frame by the shared render loop. */
  renderFrame(): void {
    const track = this.selected();
    const selectedNode = this.q("#tb-selected");
    // Naming what Load will act on: by the time you have scrolled to the
    // bottom of 653 tracks, the highlighted row is off screen too.
    selectedNode.textContent = track
      ? `→ ${track.artist ? `${track.artist} — ` : ""}${track.title ?? track.path}`
      : "select a track below to load";
    selectedNode.classList.toggle("muted", !track);

    for (const [id, panel] of Object.entries(this.panels)) {
      const snapshot = panel.deck.snapshot();
      const loaded = snapshot.state === "ready" || snapshot.state === "playing";

      const load = this.q(`button[data-load="${id}"]`) as HTMLButtonElement;
      load.disabled = track === null;

      const play = this.q(`button[data-play="${id}"]`) as HTMLButtonElement;
      play.disabled = !loaded;
      play.textContent = snapshot.state === "playing" ? "❚❚" : "▶";

      const title = this.q(`[data-title="${id}"]`);
      if (snapshot.track) {
        const artist = snapshot.track.artist ? `${snapshot.track.artist} — ` : "";
        title.textContent = `${artist}${snapshot.track.title ?? snapshot.track.path}`;
        title.classList.remove("muted");
      } else {
        title.textContent = "empty";
        title.classList.add("muted");
      }

      // Time *remaining*, not elapsed. While browsing, the only question this
      // bar has to answer is how long you have before you need the next track.
      const time = this.q(`[data-time="${id}"]`);
      if (loaded) {
        const left = Math.max(0, snapshot.duration - panel.deck.position());
        time.textContent = `−${formatDuration(left)}`;
        time.classList.toggle("warn", snapshot.state === "playing" && left <= RUNNING_OUT);
      } else {
        time.textContent = "";
        time.classList.remove("warn");
      }

      this.q(`[data-deck="${id}"]`).classList.toggle(
        "playing",
        snapshot.state === "playing",
      );
    }

    // The mixer is the single source of truth and auto-mix writes to it too, so
    // the slider follows it rather than the other way round - except while it
    // is being dragged, which would fight the hand holding it.
    const xf = this.xfader();
    if (document.activeElement !== xf) {
      const actual = String(this.mixer.getCrossfader());
      if (xf.value !== actual) xf.value = actual;
    }
  }
}
